// IMS Document Library (Session AR Item 17): browse, download and share the company's Integrated
// Management System documents (the ISO 9001/14001/45001 policy + procedure suite) without going to
// Google Drive. Read-only; the files are static repo documents served by /api/compliance-docs.
import { useEffect, useMemo, useState } from "react";
import { api, type ComplianceDocsCatalogue } from "../../lib/api";
import { useSingleTimeout } from "../../lib/useSingleTimeout";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";

export function ComplianceDocumentsPage() {
  const [catalogue, setCatalogue] = useState<ComplianceDocsCatalogue | null>(null);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const resetCopiedLater = useSingleTimeout();

  useEffect(() => {
    let live = true;
    api.complianceDocs
      .list()
      .then((c) => live && setCatalogue(c))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!catalogue) return [];
    const q = search.trim().toLowerCase();
    return catalogue.categories
      .map((cat) => ({
        ...cat,
        documents: q
          ? cat.documents.filter(
              (d) => d.displayName.toLowerCase().includes(q) || d.filename.toLowerCase().includes(q) || cat.label.toLowerCase().includes(q),
            )
          : cat.documents,
      }))
      .filter((cat) => cat.documents.length > 0);
  }, [catalogue, search]);

  const total = useMemo(() => (catalogue ? catalogue.categories.reduce((n, c) => n + c.documents.length, 0) : 0), [catalogue]);
  const shown = useMemo(() => filtered.reduce((n, c) => n + c.documents.length, 0), [filtered]);

  async function copyLink(category: string, filename: string, id: string) {
    const url = `${window.location.origin}${api.complianceDocs.href(category, filename)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      resetCopiedLater(() => setCopied((c) => (c === id ? null : c)), 2000);
    } catch {
      /* clipboard blocked; ignore (the Download/View links still work) */
    }
  }

  return (
    <div>
      <PageHeader
        className="mb-6"
        title="Document library"
        subtitle="The Integrated Management System documents (ISO 9001, 14001 and 45001). Download, view or copy a shareable link. These are the controlled master copies held in the platform."
      />

      <ListToolbar
        className="mb-5"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name or category"
        searchLabel="Search documents"
        resultCount={catalogue ? { shown, total, noun: "documents" } : undefined}
        onClear={() => setSearch("")}
      />

      {error ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the document library. Please try again in a moment.
        </p>
      ) : !catalogue ? (
        <Loader label="Loading the document library..." />
      ) : filtered.length === 0 ? (
        <EmptyState role={search.trim() ? "status" : undefined} message={search.trim() ? `No documents match "${search.trim()}".` : "No documents found."} />
      ) : (
        <div className="space-y-6">
          {filtered.map((cat) => (
            <section key={cat.category}>
              <h2 className="mb-2 font-menu text-xs uppercase tracking-widest text-ele-blue">{cat.label}</h2>
              <ul className="divide-y divide-ele-purple-accent overflow-hidden rounded-ele border border-ele-purple-accent">
                {cat.documents.map((d) => {
                  const href = api.complianceDocs.href(cat.category, d.filename);
                  const id = `${cat.category}/${d.filename}`;
                  return (
                    <li key={id} className="flex flex-wrap items-center gap-3 bg-ele-surface px-4 py-3">
                      <span className="flex-none rounded bg-ele-purple/30 px-2 py-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-strong">
                        {d.kind}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-ele-strong" title={d.filename}>
                        {d.displayName}
                      </span>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <a href={href} download={d.filename} className="text-ele-green hover:underline">
                          Download
                        </a>
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-ele-blue hover:underline">
                          View
                        </a>
                        <button type="button" onClick={() => copyLink(cat.category, d.filename, id)} className="text-ele-body hover:text-ele-strong">
                          {copied === id ? "Copied" : "Copy link"}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
